import React from 'react';
import {useIntl} from 'react-intl';
import {
	Accordion,
	TextField,
	FlexColumn,
	FlexRow,
	FlexItem,
	Label,
	Text,
	DropdownList,
	ButtonDropdownField,
	Pill,
} from '@forecast-it/design-system';
import WinProbabilityStageBands from './WinProbabilityStageBands';
import type {StageBand} from './opportunityStage';
import ResolutionCallout from './ResolutionCallout';
import type {ProjectProposal} from './types';
import {ClientPrimaryContactDropdown} from '../../../forecast-app/shared/components/dropdowns/client-primary-contact-dropdown/ClientPrimaryContactDropdown';
import Util from '../../../forecast-app/shared/util/util';

export interface OverviewSectionProps {
	proposal: ProjectProposal; // name, client_id, client_name only — D8 fields are local state, not on this type
	novaFilledFields: string[];
	clients: {id: string; name: string}[];
	persons: {id: string; backendId: number; fullName: string}[];
	projectLastSequence: number;
	stageBands: StageBand[];
	// Gates Salesperson and Contact only — Win Probability (stageBands above) is deliberately not
	// tied to this per Dylan Connerton's TNSR-635 ruling ("it has been in FC for years", not an
	// Opportunities-module feature). Computed once by the caller, same as canCreateInvoices is for
	// FinancialSetupSection.
	opportunitiesEnabled: boolean;
	clientCallout?: {question: string; candidates: string[]};
	// Lifted so AiProjectPlanningModal can send it on submit — see the Contact type's own comment.
	contact: {id: string; name: string} | null;
	// customProjectId: the old NEW_PROJECT_V2 modal this ticket replaced always sent it (both create
	// and duplicate) — lifted to the caller for the same reason as Contact above, reversing this
	// field's earlier "D8, local-only" treatment now that the old modal's own source confirms it was
	// never actually deferred scope.
	customProjectId: string;
	onProposalChange: (proposal: ProjectProposal) => void;
	onFieldUserEdited: (field: string) => void;
	onCalloutResolve: (answer: string) => void;
	onContactChange: (contact: {id: string; name: string} | null) => void;
	onCustomProjectIdChange: (customProjectId: string) => void;
	// Manual (no-document) creation only — set by AiProjectPlanningModal's handleApprove when
	// Project Name / Client is missing on submit, mirroring new_project_modal_v2's own required-
	// field behaviour. Both default to false so every other flow (SoW upload, reconciliation)
	// renders exactly as before.
	nameHasError?: boolean;
	clientHasError?: boolean;
}

// The shape ClientPrimaryContactDropdown's value/onChange trade in. Lifted to
// AiProjectPlanningModal (unlike Salesperson/Win Probability, still D8/D9 local-only — see below;
// Project ID is lifted too now, for the same reason as Contact — see its own comment above)
// because IGNITE-490's own project_client_contact.spec.js predates this modal and established
// that a Contact picked while creating a project must reach the create mutation as
// clientContactId; contact.id is already that Relay global id, unchanged from the query.
export interface Contact {
	id: string;
	name: string;
}

const OverviewSection = ({
	proposal,
	novaFilledFields,
	clients,
	persons,
	projectLastSequence,
	stageBands,
	opportunitiesEnabled,
	clientCallout,
	contact,
	customProjectId,
	onProposalChange,
	onFieldUserEdited,
	onCalloutResolve,
	onContactChange,
	onCustomProjectIdChange,
	nameHasError,
	clientHasError,
}: OverviewSectionProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const isNova = (field: string) => novaFilledFields.includes(field);

	// D8/D9: local-only state for the fields that render but never submit — Salesperson and Win
	// Probability have no home on ProjectProposal (or elsewhere) yet (see types.ts). Contact and
	// customProjectId are lifted to the caller now (see their own comments above).
	const setCustomProjectId = onCustomProjectIdChange;
	const [salesperson, setSalesperson] = React.useState<string | null>(null);
	const setContact = onContactChange;
	const [winChance, setWinChance] = React.useState(0.5);

	const salespersonOptions = React.useMemo(() => persons.map(p => ({value: p.id, label: p.fullName})), [persons]);
	const clientOptions = React.useMemo(() => clients.map(c => ({value: c.id, label: c.name})), [clients]);

	// clients[].id is a Relay global id (base64 "Client:<n>"); proposal.client_id is the decoded
	// numeric backend id — the same shape SowReviewFormPanel.tsx's own client picker sets via
	// Util.getIdFromBase64String when a client is chosen. Comparing them means decoding the option's
	// id, not stringifying the proposal's id: a plain `.toString()` against the base64 string can
	// never match a decoded number, which would leave the Client field always showing "None".
	const selectedClientOption = React.useMemo(
		() =>
			proposal.client_id == null
				? undefined
				: clientOptions.find(option => Util.getIdFromBase64String(option.value) === proposal.client_id),
		[clientOptions, proposal.client_id]
	);

	const selectClient = (option: {value: string; label: string} | null) => {
		onFieldUserEdited('client_id');
		onFieldUserEdited('client_name');
		// A contact is scoped to its client, so changing the client clears the chosen Contact —
		// mirrors CreateOpportunityModal's onClientChange.
		setContact(null);
		if (!option) {
			onProposalChange({...proposal, client_id: null, client_name: null});
			return;
		}
		const clientId = Util.getIdFromBase64String(option.value);
		if (!isNaN(clientId)) {
			onProposalChange({...proposal, client_id: clientId, client_name: option.label});
		}
	};

	return (
		<Accordion.Item
			value="overview"
			label={formatMessage({id: 'opportunity.section.overview'})}
			content=""
			data-cy="section-overview"
		>
			<FlexColumn gap="l">
				<FlexRow>
					<TextField
						data-cy="ai-project-name"
						label={formatMessage({id: 'ai_project_planning.overview_field_project_name'})}
						value={proposal.name}
						onValueChange={value => {
							onFieldUserEdited('name');
							onProposalChange({...proposal, name: value});
						}}
						maxLength={191}
						hasError={nameHasError}
						errorText={nameHasError ? formatMessage({id: 'common.required_field'}) : undefined}
					/>
					{isNova('name') && (
						<span data-cy="nova-badge-name">
							<Pill icon="sparkle">{formatMessage({id: 'sow_review.nova_filled'})}</Pill>
						</span>
					)}
				</FlexRow>
				{/* Top-aligned, matching CreateOpportunityModal: the two fields' labels stay level. */}
				<FlexRow gap="l" alignItems="flex-start">
					<FlexItem flex="1 1 0">
						<TextField
							data-cy="ai-project-id"
							label={formatMessage({id: 'ai_project_planning.overview_field_project_id'})}
							value={customProjectId}
							onValueChange={setCustomProjectId}
							maxLength={20}
							placeholder={`P${projectLastSequence + 1}`}
						/>
					</FlexItem>
					{/* Opportunities-specific field (Dylan Connerton's TNSR-635 ruling) — absent, not
						disabled, matching how FinancialSetupSection hides "Create Invoices if Available"
						for a viewer without INVOICE_CREATE rather than greying it out. */}
					{opportunitiesEnabled && (
						<FlexItem flex="1 1 0">
							<FlexColumn gap="xs">
								<Label isOptional>{formatMessage({id: 'opportunity.field.salesperson'})}</Label>
								<DropdownList>
									<DropdownList.Trigger
										data-cy="ai-salesperson"
										aria-label={formatMessage({id: 'opportunity.field.salesperson'})}
									>
										<ButtonDropdownField>
											{/* No placeholder text at all when unset — Figma's own empty state (node
												240:961, confirmed directly) renders Salesperson/Client/Contact as
												genuinely blank fields, not "None"/"Select...". */}
											<Text>{salespersonOptions.find(o => o.value === salesperson)?.label}</Text>
										</ButtonDropdownField>
									</DropdownList.Trigger>
									<DropdownList.Content align="start" useTriggerWidth>
										<DropdownList.Item data-cy="ai-salesperson-none" onClick={() => setSalesperson(null)}>
											<Text>{formatMessage({id: 'common.none'})}</Text>
										</DropdownList.Item>
										{salespersonOptions.map(option => (
											<DropdownList.Item
												key={option.value}
												data-cy={`ai-salesperson-${option.value}`}
												onClick={() => setSalesperson(option.value)}
											>
												<Text>{option.label}</Text>
											</DropdownList.Item>
										))}
									</DropdownList.Content>
								</DropdownList>
							</FlexColumn>
						</FlexItem>
					)}
				</FlexRow>
				<FlexRow gap="l">
					<FlexItem flex="1 1 0">
						<FlexColumn gap="xs">
							<Label>{formatMessage({id: 'common.client'})}</Label>
							<DropdownList>
								<DropdownList.Trigger data-cy="ai-client" aria-label={formatMessage({id: 'common.client'})}>
									<ButtonDropdownField
										hasError={clientHasError}
										errorText={clientHasError ? formatMessage({id: 'common.required_field'}) : undefined}
									>
										<Text>{selectedClientOption?.label ?? proposal.client_name}</Text>
									</ButtonDropdownField>
								</DropdownList.Trigger>
								<DropdownList.Content align="start" useTriggerWidth>
									<DropdownList.Item data-cy="ai-client-none" onClick={() => selectClient(null)}>
										<Text>{formatMessage({id: 'common.none'})}</Text>
									</DropdownList.Item>
									{clientOptions.map(option => (
										<DropdownList.Item
											key={option.value}
											data-cy={`ai-client-${option.value}`}
											onClick={() => selectClient(option)}
										>
											<Text>{option.label}</Text>
										</DropdownList.Item>
									))}
								</DropdownList.Content>
							</DropdownList>
							{isNova('client_id') && (
								<span data-cy="nova-badge-client_id">
									<Pill icon="sparkle">{formatMessage({id: 'sow_review.nova_filled'})}</Pill>
								</span>
							)}
						</FlexColumn>
					</FlexItem>
					{/* Opportunities-specific field (Dylan Connerton's TNSR-635 ruling) — same absent,
						not disabled treatment as Salesperson above. */}
					{opportunitiesEnabled && (
						<FlexItem flex="1 1 0">
							<FlexColumn gap="xs">
								<Label isOptional>{formatMessage({id: 'common.contact'})}</Label>
								{selectedClientOption ? (
									// The dropdown fetches its own contacts scoped to a Relay global client id
									// (ClientPrimaryContactDropdownQuery's $clientId), not the decoded numeric
									// client_id this proposal carries — selectedClientOption.value is that raw
									// id, already resolved above. Keyed on it so switching clients remounts the
									// picker instead of showing the previous client's stale contact list.
									<ClientPrimaryContactDropdown
										key={selectedClientOption.value}
										clientId={selectedClientOption.value}
										value={contact}
										onChange={setContact}
										hideLabel
										showPrimaryChip
										dataCy="ai-contact"
									/>
								) : (
									// Blank, like Salesperson/Client above — Figma's empty state (node 240:961,
									// confirmed directly) shows no "Select a contact" text here either.
									// Disabled until a client is chosen: an enabled-looking box would invite a
									// click that could never do anything.
									<div data-cy="ai-contact">
										<ButtonDropdownField disabled />
									</div>
								)}
							</FlexColumn>
						</FlexItem>
					)}
				</FlexRow>
				{clientCallout && (
					<ResolutionCallout
						question={clientCallout.question}
						candidates={clientCallout.candidates}
						onResolve={onCalloutResolve}
					/>
				)}
				<FlexRow>
					<FlexColumn gap="xs">
						{/* common.win_probability, not opportunity.field.win_probability or
							opportunities.update_win_probability.section ("Win Probability % and Stage") —
							the Figma frames show this label as the plain "Win Probability", not scoped to
							the Opportunities feature this section otherwise copies its composition from. */}
						<Label>{formatMessage({id: 'common.win_probability'})}</Label>
						<WinProbabilityStageBands bands={stageBands} value={winChance} onChange={setWinChance} />
					</FlexColumn>
				</FlexRow>
			</FlexColumn>
		</Accordion.Item>
	);
};

export default OverviewSection;
